import store from '@/store/index'
import * as trackedVehicleHelpers from '@/store/assets/vehicles/trackedVehicles.helpers'
import * as trackedUnitHelpers from '@/store/units/trackedUnits.helpers'
import * as assetMarkers from '@/maps/google/assetMarkers'

export default {}

/**
 * Returns the full state object of the asset tracking module.
 *
 * @returns Object
 */
export const get = () => {
  return store.getters['monitoring/monitoringModule/get']
}

/**
 * Returns the map's stored center.
 *
 * @returns google.maps.LatLng|null
 */
export const getMapCenter = () => {
  return store.getters['monitoring/monitoringModule/getMapCenter']
}

/**
 * Returns the map's stored zoom level.
 *
 * @returns int|null
 */
export const getMapZoomLevel = () => {
  return store.getters['monitoring/monitoringModule/getMapZoomLevel']
}

/**
 * Returns the array of objects of the unit's being monitored.
 *
 * @returns Object
 */
export const getMonitoredAssets = () => {
  return store.getters['monitoring/monitoringModule/getMonitoredAssets']
}

/**
 * Returns a specific monitored vehicle object or null if not found.
 *
 * @param vehicleId
 * @param assignedAs
 *
 * @return Object|null
 */
export const getMonitoredVehicle = (vehicleId, assignedAs = 'primary') => {
  const monitoredAssets = store.getters['monitoring/monitoringModule/getMonitoredAssets']
  const asset = monitoredAssets.find(asset => {
    return asset.vehicle_id === vehicleId && asset.unit_assigned_as === assignedAs
  })

  return asset === undefined ? null : asset
}

/**
 * Returns a specific monitored unit object or null if not found.
 *
 * @param unitId
 *
 * @return Object|null
 */
export const getMonitoredUnit = (unitId) => {
  const monitoredAssets = store.getters['monitoring/monitoringModule/getMonitoredAssets']
  const asset = monitoredAssets.find(asset => {
    return asset.unit_id === unitId
  })

  return asset === undefined ? null : asset
}

/**
 * Returns the id's of monitored units as an array.
 *
 * @return Array
 */
export const getAllMonitoredUnitIds = () => {
  const monitoredAssets = store.getters['monitoring/monitoringModule/getMonitoredAssets']
  return monitoredAssets.map(asset => asset.unit_id)
}

/**
 * Store the map's center.
 *
 * @param center google.maps.LatLng
 */
export const storeMapCenter = (center) => {
  store.commit(`monitoring/monitoringModule/storeMapCenter`, center)
}

/**
 * Store the map's zoom level.
 *
 * @param zoomLevel int
 */
export const storeMapZoomLevel = (zoomLevel) => {
  store.commit(`monitoring/monitoringModule/storeMapZoomLevel`, zoomLevel)
}

/**
 * Start's the re-subscription process. Calling this will
 * automatically re-subscribe all previously selected
 * vehicles/units to the SSE server.
 *
 * @return Promise
 */
export const sseResubscribe = () => {
  return store.dispatch(`monitoring/monitoringModule/sseResubscribe`)
}

/**
 * Initializes an SSE connection if one isn't setup already.
 */
export const sseInitialize = () => {
  store.commit(`monitoring/monitoringModule/sseInitialize`)
}

/**
 * If an event source is open then close it.
 */
export const sseClose = () => {
  return store.commit(`monitoring/monitoringModule/sseClose`)
}

/**
 * Request a JWT token in order to start tracking a specific vehicle.
 * Remember that a unit is actually tracked, not the vehicle directly.
 *
 * After the JWT token has been received a subscribe request should
 * be sent to the SSE/WebSocket server.
 *
 * @param vehicleId int
 * @param assignedAs string
 * @param follow boolean
 */
export const sseSubscribeVehicle = (vehicleId, assignedAs, follow = false) => {
  return store.dispatch(
    `monitoring/monitoringModule/sseSubscribeVehicle`,
    { vehicleId, assignedAs, follow }
  )
}

/**
 * Unsubscribes the unit of the specified vehicle from the SSE server
 *
 * @param vehicleId int
 * @param assignedAs string
 */
export const sseUnsubscribeVehicle = (vehicleId, assignedAs) => {
  return store.commit(
    `monitoring/monitoringModule/sseUnsubscribeVehicle`, { vehicleId, assignedAs })
}

/**
 * Request a JWT token in order to start tracking a specific unit.
 *
 * After the JWT token has been received a subscribe request should
 * be sent to the SSE/WebSocket server.
 *
 * @param unitId int
 * @param follow boolean
 */
export const sseSubscribeUnit = (unitId, follow = false) => {
  return store.dispatch(
    `monitoring/monitoringModule/sseSubscribeUnit`,
    { unitId, follow }
  )
}

/**
 * Unsubscribes the specified unit from the SSE server
 *
 * @param unitId int
 */
export const sseUnsubscribeUnit = (unitId) => {
  return store.commit(
    `monitoring/monitoringModule/sseUnsubscribeUnit`, { unitId })
}

/**
 * Unsubscribes all units and/or vehicles from the SSE server
 *
 * @param assetType Can be 'all', 'units', 'vehicles'
 */
export const sseUnsubscribeAll = (assetType = 'all') => {
  return store.commit(`monitoring/monitoringModule/sseUnsubscribeAll`, assetType)
}

/**
 * Unsubscribes all units and vehicles from the SSE server but keep
 * them in the asset list for re-subscription later on.
 */
export const sseUnsubAllButKeep = () => {
  return store.commit(`monitoring/monitoringModule/sseUnsubAllButKeep`)
}

/**
 * Unfollows all units/vehicles.
 */
export const unfollowAll = () => {
  return store.commit(`monitoring/monitoringModule/unfollowAll`)
}

/**
 * Unsubscribes the specified vehicle from the SSE server
 *
 * @param vehicleId int
 */
export const followVehicle = (vehicleId) => {
  return store.commit(
    `monitoring/monitoringModule/followVehicle`, vehicleId)
}

/**
 * Unsubscribes the specified unit from the SSE server
 *
 * @param unitId int
 */
export const followUnit = (unitId) => {
  return store.commit(
    `monitoring/monitoringModule/followUnit`, unitId)
}

export const updateActiveDriver = (data) => {
  store.commit(`monitoring/monitoringModule/updateActiveDriver`, data)
}

/**
 * Clears all markers for all units being monitored.
 */
export const clearAllMarkers = () => {
  store.commit(`monitoring/monitoringModule/clearAllMarkers`)
}

/**
 * Redraw's all markers.
 */
export const redrawAllMarkers = () => {
  store.commit(`monitoring/monitoringModule/redrawAllMarkers`)
}

/**
 * Add's or updates a vehicle or unit's marker and label. The marker
 * requires a type which is usually retrieved from the asset data, this
 * is typically either 'unit' or 'vehicle'. This type can be overwritten
 * using the overrideType argument.
 *
 * @param asset
 * @param overrideType null|string
 */
export const setMarker = (asset, overrideType = null) => {
  store.commit('monitoring/monitoringModule/setMarker', { asset, overrideType })
}

/**
 * Removes a vehicle or unit's marker and label. The marker requires a type
 * which is usually retrieved from the asset data, this is typically either
 * 'unit' or 'vehicle'. This type can be overwritten using the overrideType
 * argument.
 *
 * @param asset
 * @param overrideType null|string
 */
export const removeMarker = (asset, overrideType = null) => {
  store.commit('monitoring/monitoringModule/removeMarker', { asset, overrideType })
}

/**
 * Returns the name of the sensor which is currently being recorded, or
 * null if none is being recorded.
 *
 * @returns String|null
 */
export const getRecordingSensorName = () => {
  return store.getters['monitoring/monitoringModule/getRecordingSensorName']
}

/**
 * Returns the value index of the sensor which is currently being recorded, or
 * null if none is being recorded.
 *
 * @returns String|null
 */
export const getRecordingSensorValueIndex = () => {
  return store.getters['monitoring/monitoringModule/getRecordingSensorValueIndex']
}

/**
 * Set the name of the previous sensor that was being recorded.
 *
 * @param name
 */
export const setPrevSensorName = (name) => {
  store.commit(`monitoring/monitoringModule/setPrevSensorName`, name)
}

/**
 * Returns the name of the previous sensor that was being recorded
 *
 * @returns String|null
 */
export const getPrevRecordingSensorName = () => {
  return store.getters['monitoring/monitoringModule/getPrevRecordingSensorName']
}

/**
 * Set the name of the sensor for which we will be recording history. If the value
 * of the sensor comes through as an array an index can be provided which will then
 * be used to extract a specific element from the array. Finally, the current telemetry
 * can be provided to start the sensor data with initial values.
 *
 * @param sensor String
 * @param valueIndex Integer|null
 * @param telemetry Object|null
 */
export const recordSensorHistory = (sensor, valueIndex = null, telemetry = null) => {
  store.commit(`monitoring/monitoringModule/recordSensorHistory`, {
    sensor,
    valueIndex,
    telemetry
  })
}

/**
 * Add to the sensor's history.
 *
 * @param data Object
 */
export const addToSensorHistory = (data) => {
  store.commit(`monitoring/monitoringModule/addToSensorHistory`, data)
}

/**
 * Returns true or false depending on whether the sensor history
 * has been loaded from the history/telemetry API.
 *
 * @returns Boolean
 */
export const getSensorHistoryLoaded = () => {
  return store.getters['monitoring/monitoringModule/getSensorHistoryLoaded']
}

/**
 * Returns a sensor's history.
 *
 * @returns []
 */
export const getSensorHistory = () => {
  return store.getters['monitoring/monitoringModule/getSensorHistory']
}

/**
 * Clear the sensor's history.
 *
 * @param data Object
 */
export const clearSensorHistory = (data) => {
  store.commit(`monitoring/monitoringModule/clearSensorHistory`, data)
}

/**
 * Sets an event handler for when new sensor history data arrives.
 * The function must expect 3 arguments:
 * sensor, data, mode
 *
 * sensor = A string containing the name of the current sensor
 * data   = An array containing the data that must be added to a chart
 * mode   = 'set' or 'append' ('set' = Set the data, 'append' = append to existing data)
 *
 * @param onSensorHistoryData Function
 */
export const setOnSensorHistoryData = (onSensorHistoryData) => {
  store.commit(`monitoring/monitoringModule/setOnSensorHistoryData`, onSensorHistoryData)
}

/**
 * Returns true if the sensor has history data.
 *
 * @returns boolean
 */
export const sensorHasHistoryData = () => {
  return store.getters['monitoring/monitoringModule/sensorHasHistoryData']
}

/**
 * Clears vehicles being monitored from the map, unsubscribes them
 * from the SSE service and clears its selection from the Find Vehicles widget.
 */
export const clearVehicleMonitoring = () => {
  const trackedVehicles = trackedVehicleHelpers.trackedVehicles()
  let vehicleIds = Object.keys(trackedVehicles)
  let removeIds = []

  assetMarkers.clearTargetMarker()

  vehicleIds.forEach((vehicleId) => {
    sseUnsubscribeVehicle(trackedVehicles[vehicleId].id, trackedVehicles[vehicleId].unit_assigned_as)
  })

  vehicleIds.forEach(vehicleId => removeIds.push(parseInt(vehicleId)))
  trackedVehicleHelpers.removeTrackedVehicles(removeIds)
}

/**
 * Clears units being monitored from the map, unsubscribes them
 * from the SSE service and clears its selection from the Find Units widget.
 */
export const clearUnitMonitoring = () => {
  let unitIds = Object.keys(trackedUnitHelpers.trackedUnits())
  let removeIds = []

  unitIds.forEach((unitId) => {
    sseUnsubscribeUnit(parseInt(unitId))
  })

  unitIds.forEach(unitId => removeIds.push(parseInt(unitId)))
  trackedUnitHelpers.removeTrackedUnits(removeIds)
}

export const enableVehicleSafeZone = (id, radius, mode) => {
  return store.dispatch(`monitoring/monitoringModule/enableVehicleSafeZone`, { id, radius, mode })
}

export const disableVehicleSafeZone = (id) => {
  return store.dispatch(`monitoring/monitoringModule/disableVehicleSafeZone`, id)
}

export const enableUnitSafeZone = (id, radius, mode) => {
  return store.dispatch(`monitoring/monitoringModule/enableUnitSafeZone`, { id, radius, mode })
}

export const disableUnitSafeZone = (id) => {
  return store.dispatch(`monitoring/monitoringModule/disableUnitSafeZone`, id)
}



// WEBPACK FOOTER //
// ./src/store/monitoring/monitoringModule.helpers.js